/**
 * TranslationsOverridePanel — GM Console tab.
 *
 * Lets admins proofread and manually override Gemini-generated translations
 * for GM entities (enemies, items, world bosses, abilities). Overrides are
 * persisted server-side on the same `translations` sub-document already
 * consumed by the game engine, so nothing else has to change to pick up
 * the new copy.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Languages, Search, Save, RotateCcw, Check } from "lucide-react";

const COLLECTIONS = [
  { key: "gm_enemies",      label: "Nemici" },
  { key: "gm_items",        label: "Oggetti" },
  { key: "gm_world_bosses", label: "World Boss" },
  { key: "gm_abilities",    label: "Abilità" },
];

const LANG_LABELS = { en: "🇬🇧 EN", de: "🇩🇪 DE", fil: "🇵🇭 FIL", ru: "🇷🇺 RU" };
const LANGS = ["en", "de", "fil", "ru"];

const FIELD_LABELS = {
  name: "Nome",
  description: "Descrizione",
  lore: "Lore",
  flavor: "Flavor",
};

export default function TranslationsOverridePanel() {
  const [coll, setColl] = useState("gm_enemies");
  const [lang, setLang] = useState("en");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState({}); // { entity_id: { field: value } }
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.gmListTranslations(coll);
      setRows(r.collections?.[coll] || []);
      setDirty({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Caricamento fallito");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [coll]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const s = q.toLowerCase();
    return rows.filter(r => {
      const src = Object.values(r.source || {}).join(" ").toLowerCase();
      const tra = JSON.stringify(r.translations || {}).toLowerCase();
      return src.includes(s) || tra.includes(s) || (r.id || "").toLowerCase().includes(s);
    });
  }, [rows, q]);

  const setField = (rowId, field, value) => {
    setDirty(d => ({
      ...d,
      [rowId]: { ...(d[rowId] || {}), [field]: value },
    }));
  };

  const currentValue = (row, field) => {
    const draft = dirty[row.id]?.[field];
    if (draft != null) return draft;
    return row.translations?.[lang]?.[field] ?? "";
  };

  const save = async (row) => {
    const patch = dirty[row.id];
    if (!patch || Object.keys(patch).length === 0) return;
    setBusyId(row.id);
    try {
      const r = await api.gmOverrideTranslation(coll, row.id, lang, patch);
      setRows(prev => prev.map(x => x.id === row.id ? { ...x, translations: r.translations } : x));
      setDirty(d => { const c = { ...d }; delete c[row.id]; return c; });
      toast.success("Traduzione salvata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setBusyId(null);
    }
  };

  const resetOverride = async (row) => {
    if (!window.confirm(`Rigenerare la traduzione ${lang.toUpperCase()} di "${row.source?.name || row.id}" con Gemini al prossimo run?`)) return;
    setBusyId(row.id);
    try {
      await api.gmResetTranslationOverride(coll, row.id, lang);
      setRows(prev => prev.map(x => {
        if (x.id !== row.id) return x;
        const t = { ...(x.translations || {}) };
        t[lang] = {};
        if (t._manual_overrides) delete t._manual_overrides[lang];
        return { ...x, translations: t };
      }));
      setDirty(d => { const c = { ...d }; delete c[row.id]; return c; });
      toast.success("Override rimosso — verrà rigenerato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore reset");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="translations-override-panel">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="font-heading text-sm tracking-[0.3em] uppercase text-[#E5C34A] flex items-center gap-2">
          <Languages className="w-4 h-4" strokeWidth={1.5} /> Traduzioni manuali
        </div>
        <div className="font-mono text-[9px] tracking-widest uppercase text-[#666]">
          sovrascrivi Gemini · italiano è la fonte
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center panel p-2">
        <div className="flex gap-1" data-testid="translations-collection-tabs">
          {COLLECTIONS.map(c => (
            <button key={c.key} onClick={() => setColl(c.key)}
              data-testid={`translations-coll-${c.key}`}
              className={`px-2.5 py-1 font-heading text-[10px] tracking-widest uppercase border transition-colors
                ${coll === c.key ? "border-[#E5C34A] text-[#E5C34A] bg-[#B89C3A]/10" : "border-[#333] text-[#8c8c8c] hover:text-[#B0ADA5]"}`}>
              {c.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1 ml-auto" data-testid="translations-lang-tabs">
          {LANGS.map(l => (
            <button key={l} onClick={() => setLang(l)}
              data-testid={`translations-lang-${l}`}
              className={`px-2.5 py-1 font-mono text-[10px] tracking-widest uppercase border transition-colors
                ${lang === l ? "border-[#6FA8DC] text-[#6FA8DC] bg-[#4A7BC7]/10" : "border-[#333] text-[#8c8c8c] hover:text-[#B0ADA5]"}`}>
              {LANG_LABELS[l]}
            </button>
          ))}
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#666]" strokeWidth={1.5} />
        <input value={q} onChange={e => setQ(e.target.value)}
          data-testid="translations-search"
          placeholder="Cerca nome / descrizione..."
          className="w-full bg-[#0a0a0a] border border-[#333] pl-8 pr-3 py-2 text-xs text-[#E2DFD8] outline-none focus:border-[#B89C3A]" />
      </div>

      {loading && <div className="italic text-xs text-[#666] p-4">Caricamento traduzioni…</div>}
      {!loading && filtered.length === 0 && (
        <div className="panel p-4 italic text-xs text-[#666] text-center">Nessuna entità trovata.</div>
      )}

      <div className="space-y-2">
        {filtered.map(row => {
          const sourceFields = Object.keys(row.source || {});
          const isOverride = !!row.translations?._manual_overrides?.[lang];
          const isDirty = !!dirty[row.id];
          return (
            <div key={row.id} className="panel p-3 space-y-2" data-testid={`translation-row-${row.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-heading text-xs tracking-widest uppercase text-[#E5C34A]">
                  {row.source?.name || row.id}
                </div>
                <div className="flex items-center gap-1.5">
                  {isOverride && (
                    <span className="font-mono text-[9px] tracking-widest uppercase text-[#6E8B3D] flex items-center gap-1">
                      <Check className="w-3 h-3" /> override
                    </span>
                  )}
                  <button onClick={() => resetOverride(row)} disabled={busyId === row.id}
                    data-testid={`translation-reset-${row.id}`}
                    title="Rigenera con Gemini"
                    className="px-2 py-1 border border-[#5D2F5F] text-[#A97AB0] hover:bg-[#5D2F5F]/10 font-mono text-[9px] tracking-widest uppercase disabled:opacity-40 flex items-center gap-1">
                    <RotateCcw className="w-3 h-3" /> Reset
                  </button>
                  <button onClick={() => save(row)} disabled={!isDirty || busyId === row.id}
                    data-testid={`translation-save-${row.id}`}
                    className="px-2 py-1 border border-[#B89C3A] text-[#E5C34A] hover:bg-[#B89C3A]/10 disabled:opacity-30 font-heading text-[10px] tracking-widest uppercase flex items-center gap-1">
                    <Save className="w-3 h-3" /> Salva
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {sourceFields.map(field => (
                  <div key={field} className="space-y-1">
                    <div className="font-mono text-[9px] tracking-widest uppercase text-[#8c8c8c]">
                      {FIELD_LABELS[field] || field} · IT (fonte)
                    </div>
                    <div className="bg-[#0a0a0a] border border-[#222] px-2 py-1.5 text-[11px] text-[#B0ADA5] whitespace-pre-wrap min-h-[2rem]">
                      {row.source[field]}
                    </div>
                    <div className="font-mono text-[9px] tracking-widest uppercase text-[#6FA8DC]">
                      {FIELD_LABELS[field] || field} · {LANG_LABELS[lang]}
                    </div>
                    {field === "name" ? (
                      <input value={currentValue(row, field)}
                        data-testid={`translation-input-${row.id}-${field}`}
                        onChange={e => setField(row.id, field, e.target.value)}
                        className="w-full bg-[#0a0a0a] border border-[#333] px-2 py-1.5 text-xs text-[#E2DFD8] outline-none focus:border-[#B89C3A]" />
                    ) : (
                      <textarea value={currentValue(row, field)} rows={3}
                        data-testid={`translation-input-${row.id}-${field}`}
                        onChange={e => setField(row.id, field, e.target.value)}
                        className="w-full bg-[#0a0a0a] border border-[#333] px-2 py-1.5 text-xs text-[#E2DFD8] outline-none focus:border-[#B89C3A] resize-none" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
